import {
  ArrowDownTrayIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DocumentIcon,
  PhotoIcon,
  PlayIcon,
  SpeakerWaveIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import {
  DealResponseDto,
  FileMetadataDto,
  MessageResponseDto,
} from "../../types/types";
import { getDealById } from "../../services/api";
import { cleanDealId } from "../../utils/chat/dealStatusMessage";
import { DealCard } from "./dealCard/dealCard";
import { useConnectOnboarding } from "./dealCard/useConnectOnboarding";

interface MessageContentProps {
  message?: MessageResponseDto;
  isCurrentUser?: boolean;
  setSelectedImage: React.Dispatch<React.SetStateAction<string | null>>;
  setSelectedVideo?: React.Dispatch<React.SetStateAction<string | null>>;
  setImageCarousel: React.Dispatch<
    React.SetStateAction<{
      images: FileMetadataDto[];
      currentIndex: number;
    } | null>
  >;
  connectStatus: string | null;
  setConnectStatus: React.Dispatch<React.SetStateAction<string | null>>;
  handleWithdrawDeal?: (deal: DealResponseDto) => Promise<void>;
  dealProcessing?: Record<string, boolean>;
  getCurrentUserRole?: () => string;
  setSelectedDealForView?: React.Dispatch<
    React.SetStateAction<DealResponseDto | null>
  >;
  setShowViewDealModal?: React.Dispatch<React.SetStateAction<boolean>>;
  setShowModal?: React.Dispatch<React.SetStateAction<boolean>>;
  formatText?: (text: string) => string;
  selectedImage?: string | null;
  selectedVideo?: string | null;
  imageCarousel?: { images: FileMetadataDto[]; currentIndex: number } | null;
  isModalOnly?: boolean;
}

export const MessageContent: React.FC<MessageContentProps> = ({
  message,
  isCurrentUser = false,
  setSelectedImage,
  setSelectedVideo,
  setImageCarousel,
  connectStatus,
  setConnectStatus,
  handleWithdrawDeal,
  dealProcessing = {},
  getCurrentUserRole,
  setSelectedDealForView,
  setShowViewDealModal,
  setShowModal,
  formatText = (text) => text,
  selectedImage,
  selectedVideo,
  imageCarousel,
  isModalOnly = false,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation("chat");
  const { onboardProcessing, initiateConnectOnboarding } =
    useConnectOnboarding(setConnectStatus);

  const loginResponse = useMemo(
    () => JSON.parse(localStorage.getItem("loginResponse") || "{}"),
    [],
  );

  // Messages from older app versions end with "Deal ID: <id>"; hide it and make the text clickable.
  const dealIdText = /\s*Deal ID:\s*([A-Za-z0-9_-]+)\s*$/i;

  const extractDealIdFromMessage = (text: string) => {
    const match = text.match(dealIdText);
    return match ? cleanDealId(match[1]) : null;
  };

  const cleanMessageText = (text: string) =>
    text.replace(dealIdText, "").trim();

  // Deal messages have no bubble behind them, so their text must not be white.
  const renderClickableMessageText = (text?: string, onBubble = true) => {
    if (!text) return null;

    const dealId = extractDealIdFromMessage(text);
    const cleanText = cleanMessageText(text);
    const textColor =
      onBubble && isCurrentUser ? "!text-white" : "text-gray-900";

    if (!dealId) {
      return (
        <p style={isCurrentUser && onBubble ? { color: "#ffffff" } : undefined} className={`text-sm whitespace-pre-wrap break-words ${textColor}`}>
          {text}
        </p>
      );
    }

    return (
      <div
        className="cursor-pointer"
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/deal/view/${dealId}`);
        }}
      >
        <p style={isCurrentUser && onBubble ? { color: "#ffffff" } : undefined} className={`text-sm whitespace-pre-wrap break-words ${textColor}`}>
          {cleanText}
        </p>
      </div>
    );
  };


  const groupFilesByType = (files: FileMetadataDto[]) => {
    const images: FileMetadataDto[] = [];
    const videos: FileMetadataDto[] = [];
    const documents: FileMetadataDto[] = [];
    files.forEach((file) => {
      const ext = file.fileName.split(".").pop()?.toLowerCase();
      if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext || ""))
        images.push(file);
      else if (["mp4", "avi", "mov", "wmv"].includes(ext || ""))
        videos.push(file);
      else documents.push(file);
    });
    return { images, videos, documents };
  };

  const openImageCarousel = (images: FileMetadataDto[], startIndex: number) =>
    setImageCarousel({ images, currentIndex: startIndex });

  const closeImageCarousel = () => setImageCarousel(null);

  const navigateCarousel = (direction: "prev" | "next") => {
    if (!imageCarousel) return;
    const { images, currentIndex } = imageCarousel;
    const newIndex =
      direction === "prev"
        ? currentIndex === 0
          ? images.length - 1
          : currentIndex - 1
        : currentIndex === images.length - 1
          ? 0
          : currentIndex + 1;
    setImageCarousel({ images, currentIndex: newIndex });
  };

  const renderImageGrid = (images: FileMetadataDto[]) => {
    if (images.length === 0) return null;
    return (
      <div
        className={`grid gap-2 max-w-full overflow-hidden ${images.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}
      >
        {images.slice(0, 4).map((file, index) => (
          <div key={index} className="relative overflow-hidden">
            <img
              src={file.fileUrl}
              alt={file.fileName}
              className="w-full h-24 object-cover rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
              onClick={() => openImageCarousel(images, index)}
            />
            {index === 3 && images.length > 4 && (
              <div
                className="absolute inset-0 bg-black bg-opacity-50 rounded-lg flex items-center justify-center cursor-pointer"
                onClick={() => openImageCarousel(images, index)}
              >
                <span className="text-white text-lg font-semibold">
                  +{images.length - 4}
                </span>
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderVideoGrid = (videos: FileMetadataDto[]) => {
    if (videos.length === 0) return null;
    return (
      <div
        className={`grid gap-2 max-w-full overflow-hidden ${videos.length === 1 ? "grid-cols-1" : "grid-cols-2"}`}
      >
        {videos.map((file, index) => (
          <div key={index} className="relative overflow-hidden">
            <video
              src={file.fileUrl}
              className="w-full h-24 object-cover rounded-lg cursor-pointer"
              onClick={() => setSelectedVideo && setSelectedVideo(file.fileUrl)}
              preload="metadata"
            />
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="bg-black bg-opacity-50 rounded-full p-2">
                <PlayIcon className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileIcon = (fileName: string) => {
    const ext = fileName.split(".").pop()?.toLowerCase();
    if (["jpg", "jpeg", "png", "gif", "webp"].includes(ext || ""))
      return <PhotoIcon className="w-5 h-5" />;
    if (["mp4", "avi", "mov", "wmv"].includes(ext || ""))
      return <PlayIcon className="w-5 h-5" />;
    if (["mp3", "wav", "flac", "aac"].includes(ext || ""))
      return <SpeakerWaveIcon className="w-5 h-5" />;
    return <DocumentIcon className="w-5 h-5" />;
  };

  const renderDocumentList = (documents: FileMetadataDto[]) => {
    if (documents.length === 0) return null;
    return (
      <div className="space-y-2 max-w-full">
        {documents.map((file, index) => (
          <div
            key={index}
            className="flex items-center gap-2 bg-gray-100 rounded-lg p-2 border max-w-full overflow-hidden"
          >
            <div className="text-gray-600 flex-shrink-0">
              {getFileIcon(file.fileName)}
            </div>
            <div className="flex-1 min-w-0 overflow-hidden">
              <p className="text-xs font-medium text-gray-700 truncate">
                {file.fileName}
              </p>
              <p className="text-[10px] text-gray-500">
                {formatFileSize(file.fileSize)}
              </p>
            </div>
            <a
              href={file.fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-600 hover:text-gray-800 p-1 flex-shrink-0"
            >
              <ArrowDownTrayIcon className="w-4 h-4" />
            </a>
          </div>
        ))}
      </div>
    );
  };

  const renderMessageFiles = (files: FileMetadataDto[]) => {
    const { images, videos, documents } = groupFilesByType(files);
    return (
      <div className="space-y-3 max-w-full overflow-hidden">
        {renderImageGrid(images)}
        {renderVideoGrid(videos)}
        {renderDocumentList(documents)}
      </div>
    );
  };

  const extractUrlPreview = (text: string) => {
    const urlRegex =
      /\b((https?:\/\/)?(www\.)?[a-zA-Z0-9\-._~%]+(\.[a-zA-Z]{2,})(:[0-9]{1,5})?(\/[^\s]*)?)\b/g;
    const urls = text.match(urlRegex);
    if (!urls) return null;
    return urls[0].startsWith("http") ? urls[0] : "http://" + urls[0];
  };

  const renderUrlPreview = (url: string) => {
    const domain = new URL(url).hostname;
    return (
      <div className="border border-gray-200 rounded-lg p-3 mt-2 bg-gray-50 max-w-full">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-blue-500 rounded-sm flex items-center justify-center flex-shrink-0">
            <span className="text-white text-xs">🔗</span>
          </div>
          <div className="flex-1 min-w-0 overflow-hidden">
            <p className="text-sm font-medium text-gray-900 truncate">
              {domain}
            </p>
            <a
              href={url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-blue-600 hover:text-blue-800 break-all block"
            >
              {url}
            </a>
          </div>
        </div>
      </div>
    );
  };

  const renderDealContent = (deal: DealResponseDto | undefined) => {
    if (!deal) return null;

    const openDeal = () => {
      setSelectedDealForView?.(deal);
      setShowViewDealModal?.(true);
    };

    const editDeal = async () => {
      try {
        const fullDeal = await getDealById(deal.id);
        const dealWithCardId = {
          ...fullDeal,
          marketplaceCardId:
            (fullDeal as any).marketplaceCardId ??
            (deal as any).marketplaceCardId ??
            deal.id,
        };
        setShowViewDealModal?.(false);
        setSelectedDealForView?.(dealWithCardId);
        setShowModal?.(true);
      } catch (error) {
        console.error("Failed to fetch deal", error);
      }
    };

    return (
      <DealCard
        deal={deal}
        currentUserId={loginResponse.id}
        userRole={getCurrentUserRole?.()}
        connectStatus={connectStatus}
        isProcessing={dealProcessing[deal.id] || false}
        onboardProcessing={onboardProcessing}
        formatSport={formatText}
        onOpen={openDeal}
        onEdit={editDeal}
        onWithdraw={() => handleWithdrawDeal?.(deal)}
        onAddAccountDetails={initiateConnectOnboarding}
      />
    );
  };


  const renderMessageContent = (msg: MessageResponseDto) => {
    const hasText = msg.message && msg.message.trim();
    const hasFiles = msg.fileMetadataList && msg.fileMetadataList.length > 0;
    const urlPreview = hasText ? extractUrlPreview(msg.message) : null;
    const hasDeal = msg.dealResponseDto !== undefined;

    if (hasDeal && hasText && hasFiles) {
      return (
        <div className="space-y-2 max-w-full overflow-hidden">
          {renderMessageFiles(msg.fileMetadataList)}
          {renderClickableMessageText(msg.message, false)}
          {renderDealContent(msg.dealResponseDto)}
          {urlPreview && renderUrlPreview(urlPreview)}
        </div>
      );
    } else if (hasDeal && hasText) {
      return (
        <div className="space-y-2 max-w-full overflow-hidden">
          {renderClickableMessageText(msg.message, false)}
          {renderDealContent(msg.dealResponseDto)}
          {urlPreview && renderUrlPreview(urlPreview)}
        </div>
      );
    } else if (hasDeal && hasFiles) {
      return (
        <div className="space-y-2 max-w-full overflow-hidden">
          {renderMessageFiles(msg.fileMetadataList)}
          {renderDealContent(msg.dealResponseDto)}
        </div>
      );
    } else if (hasDeal) {
      return (
        <div className="max-w-full overflow-hidden">
          {renderDealContent(msg.dealResponseDto)}
        </div>
      );
    } else if (hasFiles && hasText) {
      return (
        <div className="space-y-2 max-w-full overflow-hidden">
          {renderMessageFiles(msg.fileMetadataList)}
          {renderClickableMessageText(msg.message)}
          {urlPreview && renderUrlPreview(urlPreview)}
        </div>
      );
    } else if (hasFiles) {
      return renderMessageFiles(msg.fileMetadataList);
    } else {
      return (
        <div className="max-w-full overflow-hidden">
          {renderClickableMessageText(msg.message || t("noMessageContent"))}
          {urlPreview && renderUrlPreview(urlPreview)}
        </div>
      );
    }
  };

  if (isModalOnly) {
    return (
      <>
        {imageCarousel && (
          <div
            className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50"
            onClick={closeImageCarousel}
          >
            <div className="relative max-w-4xl max-h-4xl p-4 w-full h-full flex items-center justify-center">
              <img
                src={imageCarousel.images[imageCarousel.currentIndex].fileUrl}
                alt={imageCarousel.images[imageCarousel.currentIndex].fileName}
                className="max-w-[500px] max-h-[500px] object-contain"
                onClick={(e) => e.stopPropagation()}
              />
              {imageCarousel.images.length > 1 && (
                <>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigateCarousel("prev");
                    }}
                    className="absolute left-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full p-3 hover:bg-opacity-75 transition-opacity"
                  >
                    <ChevronLeftIcon className="w-6 h-6" />
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      navigateCarousel("next");
                    }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 bg-black bg-opacity-50 text-white rounded-full p-3 hover:bg-opacity-75 transition-opacity"
                  >
                    <ChevronRightIcon className="w-6 h-6" />
                  </button>
                </>
              )}
              {imageCarousel.images.length > 1 && (
                <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black bg-opacity-50 text-white px-4 py-2 rounded-full">
                  <span className="text-sm">
                    {imageCarousel.currentIndex + 1} of{" "}
                    {imageCarousel.images.length}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {selectedImage && (
          <div
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
            onClick={() => setSelectedImage(null)}
          >
            <div className="relative max-w-4xl max-h-4xl p-4">
              <img
                src={selectedImage}
                alt="Full size"
                className="max-w-[500px] max-h-[500px] object-contain"
                onClick={(e) => e.stopPropagation()}
              />
              <button
                onClick={() => setSelectedImage(null)}
                className="absolute top-2 right-2 bg-black bg-opacity-50 text-white rounded-full p-2 hover:bg-opacity-75"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>
          </div>
        )}

        {selectedVideo && setSelectedVideo && (
          <div
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50"
            onClick={() => setSelectedVideo(null)}
          >
            <div className="relative max-w-4xl max-h-full p-4">
              <video
                src={selectedVideo}
                controls
                autoPlay
                className="max-w-full max-h-full"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        )}
      </>
    );
  }

  if (!message) return null;

  return renderMessageContent(message);
};
